import { skipToken } from '@reduxjs/toolkit/dist/query'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Form } from '@typesApp/DynamicComponent'
import { DefaultFormArgs, Insurer, InsurerDetailArgs, InsurerDetails, InsurerFormsType } from '@typesApp/Insurer'
import {
  useGetInsurersPublicQuery,
  useLazyGetDefaultFormsQuery,
  useLazyGetInsurerDetailsQuery,
} from '@redux/queries/insurers'
import { authTokenReadySelector, loggingOutSelector, sessionExpiredSelector } from '@redux/slices/connectorSlice'
import { logger } from '@utils/logger'

type InsurerConfig = {
  insurer: Insurer
  forms: Form[]
}

type UseInsurerConfigurationResult = {
  insurers?: Insurer[]
  isLoading: boolean
  isError: boolean
  fetchInsurerConfiguration: (insurerId: string) => Promise<InsurerConfig | undefined>
}

const PREFER_CACHE_VALUE = true

export const useInsurerConfiguration = (skip?: boolean): UseInsurerConfigurationResult => {
  const isLoggingOut = useSelector(loggingOutSelector)
  const isTokenReady = useSelector(authTokenReadySelector)
  const isSessionExpired = useSelector(sessionExpiredSelector)
  const {
    data: insurers,
    isLoading: isLoadingInsurers,
    isError: isErrorLoadingInsurers,
  } = useGetInsurersPublicQuery(!skip && !isLoggingOut && !isSessionExpired ? undefined : skipToken)
  const [fetchDetails, { isLoading: isLoadingDetails, isError: isErrorLoadingDetails }] =
    useLazyGetInsurerDetailsQuery()
  const [fetchDefaultForms, { isLoading: isLoadingForm, isError: isErrorLoadingForm }] = useLazyGetDefaultFormsQuery()

  const fetchInsurerConfiguration = useCallback(
    async (insurerId: string): Promise<InsurerConfig | undefined> => {
      // FIXME: until insurer details API is using apikey
      if (!isTokenReady) {
        return undefined
      }

      const insurer = insurers?.find(i => i.id === insurerId)
      if (!insurer) {
        return undefined
      }

      const fallbackToDefaultForms = async (insurer: Insurer) => {
        const args = { insuranceProviderId: insurer.insuranceProviderId } as DefaultFormArgs

        const forms = await fetchDefaultForms(args, PREFER_CACHE_VALUE).unwrap()
        return { insurer, forms }
      }

      const initCustomForms = async (insurer: Insurer): Promise<InsurerConfig> => {
        const args = { insurerId: insurer.id } as InsurerDetailArgs

        try {
          const details = await fetchDetails(args, PREFER_CACHE_VALUE).unwrap()
          if (hasCustomForms(details)) {
            return { insurer, forms: details.dynamicFormsConfig?.formGroups }
          } else {
            return await fallbackToDefaultForms(insurer)
          }
        } catch (error) {
          logger.warn(error)
          return await fallbackToDefaultForms(insurer)
        }
      }

      const initDefaultForms = async (insurer: Insurer) => {
        const args = { insuranceProviderId: insurer.insuranceProviderId } as DefaultFormArgs

        const forms = await fetchDefaultForms(args, PREFER_CACHE_VALUE).unwrap()
        return { insurer, forms }
      }

      if (insurer.dynamicFormsType === InsurerFormsType.CUSTOM) {
        return initCustomForms(insurer)
      } else {
        return initDefaultForms(insurer)
      }
    },
    [fetchDetails, fetchDefaultForms, insurers, isTokenReady]
  )

  return {
    isLoading: isLoadingInsurers || isLoadingDetails || isLoadingForm,
    isError: isErrorLoadingInsurers || isErrorLoadingDetails || isErrorLoadingForm,
    insurers,
    fetchInsurerConfiguration,
  }
}

const hasCustomForms = (details: InsurerDetails): boolean => {
  return Array.isArray(details.dynamicFormsConfig?.formGroups) && details.dynamicFormsConfig.formGroups.length > 0
}
